import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type CardSize = 'sm' | 'md';

type StyledProps = {
  $size: CardSize;
  $active?: boolean;
  $clickable?: boolean;
  $color?: string;
  $backgroundColor?: string;
  $borderColor?: string;
  $activeBorderColor?: string;
};

const sizeStyles: Record<CardSize, ReturnType<typeof css>> = {
  sm: css`
    min-height: 72px;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 13px;
  `,
  md: css`
    min-height: 88px;
    padding: 16px;
    border-radius: 10px;
    font-size: 14px;
  `,
};

export const StyledCardLink = styled('a', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  font-family: 'Mangueira', sans-serif;
  text-decoration: none;
  background: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  color: ${({ $color }) => $color || theme.colors.primaryText};
  border: 1px solid
    ${({ $active, $borderColor, $activeBorderColor }) =>
      $active
        ? $activeBorderColor || theme.colors.mediumPurple
        : $borderColor || '#E8E8E8'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  cursor: pointer;

  ${({ $size }) => sizeStyles[$size]}

  &:hover {
    border-color: ${theme.colors.mediumPurple};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

export const StyledCardButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  font-family: 'Mangueira', sans-serif;
  text-align: left;
  background: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  color: ${({ $color }) => $color || theme.colors.primaryText};
  border: 1px solid
    ${({ $active, $borderColor, $activeBorderColor }) =>
      $active
        ? $activeBorderColor || theme.colors.mediumPurple
        : $borderColor || '#E8E8E8'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  cursor: pointer;

  ${({ $size }) => sizeStyles[$size]}

  &:hover {
    border-color: ${theme.colors.mediumPurple};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

export const StyledCard = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  font-family: 'Mangueira', sans-serif;
  background: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  color: ${({ $color }) => $color || theme.colors.primaryText};
  border: 1px solid
    ${({ $active, $borderColor, $activeBorderColor }) =>
      $active
        ? $activeBorderColor || theme.colors.mediumPurple
        : $borderColor || '#E8E8E8'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  ${({ $size }) => sizeStyles[$size]}
`;

export const Main = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.mediumPurple};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const Title = styled.span`
  font-weight: 600;
  line-height: 1.3;
`;

export const Trailing = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${theme.colors.mediumPurple};

  svg {
    width: 16px;
    height: 16px;
  }
`;
